import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FiAlertTriangle, FiLock, FiX, FiShield } from "react-icons/fi";

// Animations
const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const slideUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
`;

const pulse = keyframes`
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
`;

const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`;

// Styled Components
const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 23, 42, 0.7);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    animation: ${fadeIn} 0.2s ease-out;
    padding: 20px;
`;

const ModalContainer = styled.div`
    width: 100%;
    max-width: 420px;
    background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
    border-radius: 20px;
    box-shadow:
        0 25px 50px -12px rgba(0, 0, 0, 0.25),
        0 0 0 1px rgba(220, 53, 69, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.8);
    animation: ${slideUp} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    overflow: hidden;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(90deg, #dc3545, #ff6b6b, #dc3545);
        background-size: 200% 100%;
        animation: ${shimmer} 2s linear infinite;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: rgba(100, 116, 139, 0.1);
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    z-index: 1;

    &:hover {
        background: rgba(220, 53, 69, 0.1);
        color: #dc3545;
        transform: rotate(90deg);
    }
`;

const IconWrapper = styled.div`
    width: 72px;
    height: 72px;
    margin: 0 auto 16px;
    background: linear-gradient(135deg, rgba(220, 53, 69, 0.15) 0%, rgba(220, 53, 69, 0.05) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    animation: ${pulse} 2s ease-in-out infinite;

    &::after {
        content: '';
        position: absolute;
        inset: -4px;
        border-radius: 50%;
        border: 2px dashed rgba(220, 53, 69, 0.3);
    }

    svg {
        width: 32px;
        height: 32px;
        color: #dc3545;
    }
`;

const ModalHeader = styled.div`
    padding: 28px 24px 0;
    text-align: center;
`;

const Title = styled.h3`
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 8px;
    letter-spacing: -0.02em;
`;

const Description = styled.p`
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 14px;
    color: #64748b;
    margin: 0;
    line-height: 1.5;
`;

const ModalBody = styled.div`
    padding: 24px;
`;

const WarningBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    background: linear-gradient(135deg, rgba(255, 193, 7, 0.12) 0%, rgba(255, 152, 0, 0.08) 100%);
    border: 1px solid rgba(255, 193, 7, 0.3);
    border-radius: 12px;
    margin-bottom: 20px;

    svg {
        color: #f59e0b;
        flex-shrink: 0;
    }

    span {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        font-size: 13px;
        color: #92400e;
        font-weight: 500;
    }
`;

const InputWrapper = styled.div`
    position: relative;
    margin-bottom: 8px;
`;

const InputIcon = styled.div`
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    transition: color 0.2s ease;
`;

const PasswordInput = styled.input`
    width: 100%;
    padding: 14px 14px 14px 44px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 15px;
    color: #1e293b;
    background: #f8fafc;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    outline: none;
    transition: all 0.2s ease;
    box-sizing: border-box;

    &::placeholder {
        color: #94a3b8;
    }

    &:focus {
        background: #ffffff;
        border-color: #dc3545;
        box-shadow: 0 0 0 4px rgba(220, 53, 69, 0.1);
    }

    &:focus + ${InputIcon} {
        color: #dc3545;
    }
`;

const InputLabel = styled.label`
    display: block;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;

    svg {
        width: 14px;
        height: 14px;
        color: #94a3b8;
    }
`;

const ButtonsContainer = styled.div`
    display: flex;
    gap: 12px;
    margin-top: 24px;

    @media (max-width: 400px) {
        flex-direction: column;
    }
`;

const Button = styled.button`
    flex: 1;
    padding: 14px 20px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-size: 14px;
    font-weight: 600;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:active {
        transform: scale(0.98);
    }
`;

const ConfirmButton = styled(Button)`
    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
    color: #ffffff;
    border: none;
    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);

    &:hover {
        background: linear-gradient(135deg, #c82333 0%, #b21f2d 100%);
        box-shadow: 0 6px 16px rgba(220, 53, 69, 0.4);
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0) scale(0.98);
    }
`;

const CancelButton = styled(Button)`
    background: #ffffff;
    color: #475569;
    border: 2px solid #e2e8f0;

    &:hover {
        background: #f8fafc;
        border-color: #cbd5e1;
        color: #1e293b;
    }
`;

const ModalConfirmacaoSenha = ({ isOpen, onClose, onConfirm, hasCancelPassword }) => {
    const { t } = useTranslation('common');
    const [senha, setSenha] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen && hasCancelPassword && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen, hasCancelPassword]);

    useEffect(() => {
        if (!isOpen) {
            setSenha("");
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleConfirm = () => {
        // Validação: se precisa de senha e está vazia, mostra erro
        if (hasCancelPassword && !senha.trim()) {
            toast.error(t('cancelOrder.passwordEmpty'), {
                autoClose: 3000,
            });
            inputRef.current?.focus();
            return;
        }
        onConfirm(senha);
        setSenha("");
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleConfirm();
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    return (
        <ModalOverlay onClick={handleOverlayClick}>
            <ModalContainer onKeyDown={handleKeyDown}>
                <CloseButton onClick={onClose} aria-label={t('buttons.close')}>
                    <FiX size={18} />
                </CloseButton>

                <ModalHeader>
                    <IconWrapper>
                        <FiAlertTriangle />
                    </IconWrapper>
                    <Title>{t('cancelOrder.confirmTitle')}</Title>
                    <Description>
                        {hasCancelPassword
                            ? t('cancelOrder.passwordRequired')
                            : t('cancelOrder.confirmWithoutPassword')
                        }
                    </Description>
                </ModalHeader>

                <ModalBody>
                    <WarningBadge>
                        <FiAlertTriangle size={18} />
                        <span>{t('cancelOrder.warningMessage')}</span>
                    </WarningBadge>

                    {hasCancelPassword && (
                        <>
                            <InputLabel>
                                <FiShield />
                                {t('cancelOrder.title')}
                            </InputLabel>
                            <InputWrapper>
                                <PasswordInput
                                    ref={inputRef}
                                    type="password"
                                    value={senha}
                                    onChange={(e) => setSenha(e.target.value)}
                                    placeholder={t('cancelOrder.passwordPlaceholder')}
                                    autoComplete="off"
                                />
                                <InputIcon>
                                    <FiLock size={18} />
                                </InputIcon>
                            </InputWrapper>
                        </>
                    )}

                    <ButtonsContainer>
                        <CancelButton onClick={onClose}>
                            {t('cancelOrder.cancel')}
                        </CancelButton>
                        <ConfirmButton onClick={handleConfirm}>
                            <FiAlertTriangle size={16} />
                            {t('cancelOrder.confirm')}
                        </ConfirmButton>
                    </ButtonsContainer>
                </ModalBody>
            </ModalContainer>
        </ModalOverlay>
    );
};

export default ModalConfirmacaoSenha;
